import * as React from "react";
import { style } from "typestyle";
import {
  customerHasPermissions,
  siteHasPermissions,
  systemHasPermissions,
} from "../../../acl/utils";

import ForwardBoldArrow from "../../icons/ForwardBoldArrow";
import {
  generate,
  ROUTE_ONBOARDING_CONTINUE,
  ROUTE_SITE_AREA_EDIT,
  ROUTE_SITE_AREA_EXPORT,
  ROUTE_SITE_FLIGHT_REPORT_RECIPIENTS_EDIT,
  ROUTE_SITE_MATRIX_CALCULATOR,
  ROUTE_SITE_WORKFLOW_LIST_BY_TYPE,
  ROUTE_SRS_CONFIG,
} from "../../../redux/modules/route/routes";
import { Customer } from "../../../redux/modules/customers/reducer";
import { AclState } from "../../../acl/reducer";
import { Workflow } from "../../../redux/modules/workflow/reducer";
import {
  WORKFLOW_TYPE_BASE_SURFACE,
  WORKFLOW_TYPE_GCP,
  WORKFLOW_TYPE_OVERLAYS,
  WORKFLOW_TYPE_PREPROCESSED_DATA,
  WORKFLOW_TYPE_TARGET_SURFACE,
  WORKFLOW_TYPE_UPLOAD_DEM,
} from "../../../redux/modules/workflow/constants";
import {
  getWorkflowDescription,
  getWorkflowLogo,
  getWorkflowTitle,
  getWorkflowViewPermission,
} from "../../../redux/modules/workflow/strings";
import {
  createCompositeSiteId,
  Site,
} from "../../../redux/modules/sites/reducer";
import DashboardButtonGroup from "./DashboardButtonGroup";
import DashboardButton from "./DashboardButton";
import moment from "moment";
import { CurrentSiteState } from "../../../redux/modules/currentSite/reducer";
import config from "../../../config";
import { AnyAction } from "redux";
import InfoIcon from "../../icons/Info";
import Button from "../../components/Button";
import { Blue, White } from "@traceair/tds";

export type DashboardContentProps = {
  pushRoute: (url: string) => AnyAction;

  // send a log to bws that pilot app link is clicked
  logPilotAppLinkClick?: () => void;
  siteCreationWorkflows: Workflow[];
  customerId: string;
  siteId: string;
  customer: Customer | null;
  acl: AclState;
  site: Site;
  currentSite: CurrentSiteState;
};

export class DashboardPageContent extends React.Component<DashboardContentProps> {
  render(): JSX.Element {
    return (
      <div>
        {this.renderTopButtons()}
        <div
          className={style({
            $nest: {
              "&>*": {
                marginTop: "40px",
              },
              "&>*:first-child": {
                marginTop: "0",
              },
            },
          })}
        >
          {this.renderProjectRelatedDataButtonGroup()}
          {this.renderSiteSetUpButtonGroup()}
          {this.renderFlightMarkersButtonGroup()}
        </div>
      </div>
    );
  }

  renderTopButtons(): JSX.Element | null {
    const { customer, acl } = this.props;
    if (!customer) {
      return null;
    }
    return (
      <div
        className={style({
          display: "flex",
          marginBottom: "40px",
          $nest: { "&>*:not(:first-child)": { marginLeft: "16px" } },
        })}
      >
        {customerHasPermissions(
          acl,
          ["BACKOFFICE_SITE_CREATION_WORKFLOW_START"],
          customer.id,
        ) && this.renderContinueSiteCreationButton()}
        {systemHasPermissions(acl, ["BACKOFFICE_PILOT_TUTORIALS_ACCESS"]) &&
          this.renderTutorialsButton()}
      </div>
    );
  }

  renderTutorialsButton(): JSX.Element | "" {
    const { pilotTutorialsLink } = config;
    return (
      pilotTutorialsLink && (
        <Button
          label="Help videos & Tutorials"
          icon={<InfoIcon color={Blue} />}
          size={"big"}
          onClick={() => {
            window.open(pilotTutorialsLink, "_blank");
          }}
        />
      )
    );
  }

  renderContinueSiteCreationButton(): JSX.Element | null {
    const { pushRoute, siteCreationWorkflows, customerId, siteId } = this.props;
    const siteCreationWorkflow = siteCreationWorkflows[0];
    if (!siteCreationWorkflow) {
      return null;
    }
    const siteLabel =
      siteCreationWorkflow.status === "SUCCESSFUL"
        ? "View Onboarding Data"
        : "Continue Site Registration";
    return (
      <Button
        icon={<ForwardBoldArrow color={White} size={"32px"} />}
        label={siteLabel}
        size={"big"}
        type={"primary"}
        onClick={() => {
          pushRoute(
            generate(ROUTE_ONBOARDING_CONTINUE, {
              customerId,
              siteId,
              workflowId: siteCreationWorkflow.id,
            }),
          );
        }}
      />
    );
  }

  renderProjectRelatedDataButtonGroup(): JSX.Element | null {
    const { site, acl } = this.props;
    if (!site) {
      return null;
    }
    const workflowList = [
      WORKFLOW_TYPE_TARGET_SURFACE,
      WORKFLOW_TYPE_OVERLAYS,
      WORKFLOW_TYPE_BASE_SURFACE,
      WORKFLOW_TYPE_PREPROCESSED_DATA,
      WORKFLOW_TYPE_UPLOAD_DEM,
    ];
    return (
      <DashboardButtonGroup title="Add Project-related data">
        {workflowList.map((workflowType) => {
          return (
            siteHasPermissions(
              acl,
              [getWorkflowViewPermission(workflowType)],
              createCompositeSiteId(site),
            ) && this.renderDashboardWorkflowButton(workflowType)
          );
        })}
      </DashboardButtonGroup>
    );
  }

  renderDashboardWorkflowButton(workflowType: string): JSX.Element {
    const { pushRoute, customerId, siteId } = this.props;

    return (
      <div
        key={workflowType}
        className={style({
          display: "flex",
          $nest: {
            "&>*": {
              marginLeft: "10px",
            },
            "&>*:first-child": {
              marginLeft: "0",
              flex: "1",
            },
          },
        })}
      >
        <DashboardButton
          logoUrl={getWorkflowLogo(workflowType)}
          title={getWorkflowTitle(workflowType)}
          subtitle={getWorkflowDescription(workflowType)}
          onClick={() => {
            pushRoute(
              generate(ROUTE_SITE_WORKFLOW_LIST_BY_TYPE, {
                customerId,
                siteId,
                workflowType,
              }),
            );
          }}
        />
      </div>
    );
  }

  renderSiteSetUpButtonGroup(): JSX.Element | null {
    const { site, acl } = this.props;
    if (!site) {
      return null;
    }

    return (
      <DashboardButtonGroup title="Site Set Up and Drone flight">
        {siteHasPermissions(
          acl,
          ["BACKOFFICE_SITE_CREATION_WORKFLOW_VIEW"],
          createCompositeSiteId(site),
        ) && this.renderFlightPlanButton()}
        {siteHasPermissions(
          acl,
          ["BACKOFFICE_SITE_EDIT"],
          createCompositeSiteId(site),
        ) && this.renderEditSiteButton()}
        {siteHasPermissions(
          acl,
          ["BACKOFFICE_SRS_EDIT"],
          createCompositeSiteId(site),
        ) && this.renderSrsConfigurationButton()}
        {siteHasPermissions(
          acl,
          ["BACKOFFICE_MATRIX_CALCULATOR"],
          createCompositeSiteId(site),
        ) && this.renderMatrixCalculatorButton()}
        {siteHasPermissions(
          acl,
          [
            "BACKOFFICE_API_MAIL_RECIPIENTS_VIEW",
            "BACKOFFICE_API_MAIL_RECIPIENTS_UPDATE",
          ],
          createCompositeSiteId(site),
        ) && this.renderFlightReportRecipientsButton()}
      </DashboardButtonGroup>
    );
  }

  renderSrsConfigurationButton(): JSX.Element {
    const { pushRoute, customerId, siteId } = this.props;

    return (
      <DashboardButton
        title="Configure Site CRS"
        subtitle="Check or modify Site CRS"
        logoUrl="./logo-dashboard-crs.svg"
        statusIcon="./icon-time.svg"
        onClick={() => {
          pushRoute(
            generate(ROUTE_SRS_CONFIG, {
              customerId,
              siteId,
            }),
          );
        }}
      />
    );
  }

  renderFlightPlanButton(): JSX.Element {
    const { pushRoute, customerId, siteId, currentSite } = this.props;
    const updated =
      currentSite && currentSite.data && currentSite.data.boundary
        ? "Updated " +
          moment
            .duration(
              moment().diff(
                moment(currentSite.data.boundary.properties.updated),
              ),
            )
            .humanize() +
          " ago"
        : null;

    return (
      <DashboardButton
        title="Download flight border (KML)"
        subtitle="Prior to flight, visualize or download the most recent flight boundaries."
        logoUrl="./logo-dashboard-flight-area.svg"
        statusIcon="./icon-time.svg"
        statusText={updated}
        onClick={() => {
          pushRoute(
            generate(ROUTE_SITE_AREA_EXPORT, {
              customerId,
              siteId,
            }),
          );
        }}
      />
    );
  }

  renderEditSiteButton(): JSX.Element {
    const { pushRoute, customerId, siteId } = this.props;
    return (
      <DashboardButton
        title="Jobsite Area"
        subtitle="Edit the jobsite boundaries to define the drone mapping area."
        logoUrl="./logo-dashboard-edit-area.svg"
        onClick={() => {
          pushRoute(
            generate(ROUTE_SITE_AREA_EDIT, {
              customerId,
              siteId,
            }),
          );
        }}
      />
    );
  }

  renderMatrixCalculatorButton(): JSX.Element {
    const { pushRoute, customerId, siteId } = this.props;
    return (
      <DashboardButton
        title="Convert GPS coordinates"
        subtitle="Convert GPS coordinates to the system you need (NAD, WGS or Assumed)"
        logoUrl="./logo-dashboard-matrix-calculator.svg"
        onClick={() => {
          pushRoute(
            generate(ROUTE_SITE_MATRIX_CALCULATOR, {
              customerId,
              siteId,
            }),
          );
        }}
      />
    );
  }

  renderFlightReportRecipientsButton(): JSX.Element {
    const { pushRoute, customerId, siteId } = this.props;
    return (
      <DashboardButton
        title="Flight Report Recipients"
        subtitle="Manage recipients of Quality Report and Flight Marker Request emails."
        logoUrl="./logo-dashboard-flight-report-recipients.svg"
        onClick={() => {
          pushRoute(
            generate(ROUTE_SITE_FLIGHT_REPORT_RECIPIENTS_EDIT, {
              customerId,
              siteId,
            }),
          );
        }}
      />
    );
  }

  renderFlightMarkersButtonGroup(): JSX.Element | null {
    const { site, acl } = this.props;
    if (!site) {
      return null;
    }

    return (
      <DashboardButtonGroup title="Flight Markers">
        {siteHasPermissions(
          acl,
          ["BACKOFFICE_GCP_WORKFLOW_START"],
          createCompositeSiteId(site),
        ) && this.renderFlightMarkersButton()}

        {siteHasPermissions(
          acl,
          [getWorkflowViewPermission(WORKFLOW_TYPE_GCP)],
          createCompositeSiteId(site),
        ) && this.renderDashboardWorkflowButton(WORKFLOW_TYPE_GCP)}

        {siteHasPermissions(
          acl,
          ["BACKOFFICE_FLIGHT_MARKERS_MANAGE"],
          createCompositeSiteId(site),
        ) && (
          <>
            {this.renderManageFlightMarkersButton()}
            {this.renderAddFlightMarkersButton()}
          </>
        )}
      </DashboardButtonGroup>
    );
  }

  renderFlightMarkersButton(): JSX.Element {
    const { customerId, siteId } = this.props;
    return (
      <DashboardButton
        title="Flight Markers Map"
        subtitle="Navigate to flight marker in order to set, verify, or repair."
        logoUrl="./logo-dashboard-gcp-app.svg"
        onClick={() => {
          window.location.href = `${
            config.endpoints.gcpApp
          }/#/customers/${customerId}/sites/${siteId}/current-gcp-request?return_url=${encodeURIComponent(
            window.location.href,
          )}&from=backoffice_dashboard`;
        }}
      />
    );
  }

  renderManageFlightMarkersButton(): JSX.Element | null {
    const { customerId, siteId } = this.props;
    return (
      <DashboardButton
        title="Manage Flight Markers (for GIS Team)"
        subtitle="Archive and adjust flight marker types."
        logoUrl="./logo-dashboard-manage-flight-markers.svg"
        onClick={() => {
          window.location.href = `${
            config.endpoints.gcpRegistryApp
          }/${customerId}/${siteId}?return_url=${encodeURIComponent(
            window.location.href,
          )}`;
        }}
      />
    );
  }

  renderAddFlightMarkersButton(): JSX.Element | null {
    const { customerId, siteId } = this.props;
    return (
      <DashboardButton
        title="Add Flight Markers (for GIS Team)"
        subtitle="Upload a CSV or add GCPs manually."
        logoUrl="./logo-dashboard-add-flight-markers.svg"
        onClick={() => {
          window.location.href = `${
            config.endpoints.gcpApp
          }/#/customers/${customerId}/sites/${siteId}/add?return_url=${encodeURIComponent(
            window.location.href,
          )}`;
        }}
      />
    );
  }
}
